/**
 * PATH: packages/email-hub/src/feature/reader-header/actions/overflowGroups.ts
 * FILE_ID: EMAILHUB-READERHEADER-ACTIONS-OVERFLOWGROUPS-001
 * PURPOSE: Own the More-menu section order, headings, and spillover mapping.
 * ROLE IN ARCHITECTURE: Pure registry companion data (split from registry.ts, 500-line ceiling).
 * MAIN EXPORTS: READER_OVERFLOW_GROUP_ORDER, READER_OVERFLOW_GROUP_LABELS, readerOverflowGroup.
 * NON-RESPONSIBILITIES: This file does not define actions or render menus.
 * NOTES FOR FUTURE AI: Risk stays last (plan "More menu"); grouping is registry-only data.
 */
import type { ReaderActionId, ReaderOverflowGroup } from '../model/types'
import { getReaderAction } from './registry'

/**
 * Compact-density spillover: core-slot actions that fold into More when the
 * bar has no room still need a menu section. Kept here (not in components)
 * so the menu grouping is registry-only data.
 */
const SPILLOVER_GROUP: Partial<Record<ReaderActionId, ReaderOverflowGroup>> = {
  back: 'navigate',
  previous: 'navigate',
  next: 'navigate',
  find: 'navigate',
  expand: 'navigate',
  reply: 'communication',
  reply_all: 'communication',
  forward: 'communication',
  done: 'communication',
}

/**
 * More-menu section render order (plan "More menu and long-tail actions").
 * Navigate holds compact-density spillover; Risk is always last and visually
 * separated by the menu component.
 */
export const READER_OVERFLOW_GROUP_ORDER: readonly ReaderOverflowGroup[] = [
  'navigate',
  'communication',
  'workflow',
  'access',
  'state',
  'view',
  'risk',
]

/** Operator-facing section headings; `navigate` renders without a heading. */
export const READER_OVERFLOW_GROUP_LABELS: Readonly<
  Record<ReaderOverflowGroup, string | null>
> = {
  navigate: null,
  communication: 'Communication',
  workflow: 'Workflow',
  access: 'Access',
  state: 'State',
  view: 'View',
  risk: null,
}

/** Resolve the More-menu section for any action id, including core spillover. */
export function readerOverflowGroup(id: ReaderActionId): ReaderOverflowGroup {
  return getReaderAction(id).overflowGroup ?? SPILLOVER_GROUP[id] ?? 'workflow'
}
